import { useEffect, useState, useCallback } from "react";
import { useControl } from "react-map-gl/mapbox";

/**
 * Native Mapbox IControl implementation for the settings button.
 * Uses Mapbox's background-image pattern (child div.mapboxgl-ctrl-icon)
 * so the icon is automatically centered and styled like native controls.
 */
class SettingsMapControl {
  constructor(onToggle) {
    this._onToggle = onToggle;
  }

  onAdd() {
    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

    this._button = document.createElement("button");
    this._button.type = "button";
    this._button.ariaLabel = "Adjust nearby range";
    this._button.addEventListener("click", (e) => {
      e.stopPropagation();
      this._onToggle();
    });

    // Mapbox pattern: child div with mapboxgl-ctrl-icon gets
    // background-position:50%, width/height:100%, display:block
    // — automatically centering whatever background-image CSS provides.
    const icon = document.createElement("div");
    icon.className = "mapboxgl-ctrl-icon settings-ctrl-icon";
    this._button.appendChild(icon);

    this._container.appendChild(this._button);
    return this._container;
  }

  onRemove() {
    this._button?.removeEventListener("click", this._onToggle);
    this._container?.remove();
  }
}

/**
 * React wrapper for the native Mapbox settings control.
 * Manages open/closed state and exposes the button element via ref for popover positioning.
 *
 * @param {Object} props
 * @param {string} props.position - Mapbox control position (default: "top-right")
 * @param {function} props.onOpenChange - Callback when open state changes: (isOpen) => void
 * @param {React.RefObject} props.buttonRef - Ref that receives the button DOM element
 */
export default function SettingsControl({
  position = "top-right",
  onOpenChange,
  buttonRef,
  closeRef,
}) {
  // Use state (not ref) so React re-renders on toggle,
  // ensuring useEffect dependencies work correctly for CSS class sync.
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const ctrl = useControl(() => new SettingsMapControl(handleToggle), {
    position,
  });

  // Expose close function for external use (click-outside)
  useEffect(() => {
    if (closeRef) {
      closeRef.current = () => setIsOpen(false);
    }
  }, [closeRef]);

  // Expose the button element for popover positioning
  useEffect(() => {
    if (ctrl?._button && buttonRef) {
      buttonRef.current = ctrl._button;
    }
  }, [ctrl, buttonRef]);

  // Notify parent of open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Toggle active CSS class on the native button when open state changes
  useEffect(() => {
    if (ctrl?._button) {
      ctrl._button.classList.toggle("settings-ctrl-active", isOpen);
    }
  }, [isOpen, ctrl]);

  return null;
}
